import { Platform } from 'react-native';
import { supabase } from './supabase';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';
const FEED_TOKEN_REQUEST_TIMEOUT_MS = 8000;
const GENERIC_FEED_TOKEN_ERROR = 'Unable to generate RSS/Atom URL right now. Please try again.';

export interface FeedTokenResponse {
    token: string;
    created_at: string;
}

function generateClientFeedToken(): string {
    const randomUuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '')
        : '';
    const fallbackRandom = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
    const entropy = (randomUuid || fallbackRandom).slice(0, 48);
    return `pzft_${entropy}`;
}

function trimTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

function getConfiguredBaseUrl(): string {
    return trimTrailingSlash(process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL);
}

function isLocalWebDevHost(): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

function pushCandidate(candidates: string[], endpoint: string): void {
    if (!candidates.includes(endpoint)) {
        candidates.push(endpoint);
    }
}

function getFeedTokenEndpointCandidates(): string[] {
    const candidates: string[] = [];

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Local Expo web dev usually has no /api proxy; fallback to local backend.
        if (isLocalWebDevHost()) {
            pushCandidate(candidates, `${DEFAULT_LOCAL_API_BASE_URL}/api/auth/feed-token`);
        }

        // Preferred on hosted web when frontend and API share origin/proxy.
        pushCandidate(candidates, '/api/auth/feed-token');
    }

    // Native and final fallback on web.
    const configuredBaseEndpoint = `${getConfiguredBaseUrl()}/api/auth/feed-token`;
    pushCandidate(candidates, configuredBaseEndpoint);

    return candidates;
}

async function parseErrorResponse(response: Response): Promise<string> {
    let message = `Request failed (${response.status})`;

    try {
        const body = await response.json();
        if (body?.detail) {
            message = String(body.detail);
        } else if (body?.message) {
            message = String(body.message);
        }
    } catch {
        try {
            const text = await response.text();
            if (text) {
                message = text;
            }
        } catch {
            // Keep default message
        }
    }

    if (response.status === 401) {
        return 'Your session expired. Please sign in again.';
    }

    return message;
}

async function fetchOrCreateFeedTokenViaSupabase(): Promise<FeedTokenResponse> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
        throw new Error(sessionError.message || 'Failed to get current session.');
    }
    if (!session?.user?.id) {
        throw new Error('You need to sign in to copy the RSS/Atom URL.');
    }

    const { data: existingToken, error: existingError } = await supabase
        .from('feed_token')
        .select('token, created_at')
        .limit(1)
        .maybeSingle();

    if (existingError) {
        throw new Error(existingError.message || 'Failed to fetch feed token.');
    }

    if (existingToken?.token) {
        return existingToken as FeedTokenResponse;
    }

    const token = generateClientFeedToken();
    const { data: insertedToken, error: insertError } = await supabase
        .from('feed_token')
        .insert({ user_id: session.user.id, token })
        .select('token, created_at')
        .single();

    if (!insertError && insertedToken?.token) {
        return insertedToken as FeedTokenResponse;
    }

    // Race-safe retry when another client created token first.
    const { data: tokenAfterRace, error: retryError } = await supabase
        .from('feed_token')
        .select('token, created_at')
        .limit(1)
        .maybeSingle();

    if (retryError || !tokenAfterRace?.token) {
        const message = insertError?.message
            || retryError?.message
            || 'Failed to create feed token.';
        throw new Error(message);
    }

    return tokenAfterRace as FeedTokenResponse;
}

function isAuthRelatedMessage(message: string): boolean {
    const normalized = message.toLowerCase();
    return normalized.includes('sign in')
        || normalized.includes('session')
        || normalized.includes('unauthorized')
        || normalized.includes('invalid token');
}

export async function getSessionAccessToken(): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        throw new Error(error.message || 'Failed to get current session.');
    }

    if (!session?.access_token) {
        throw new Error('You need to sign in to copy the RSS/Atom URL.');
    }

    return session.access_token;
}

export async function fetchOrCreateFeedToken(accessToken: string): Promise<FeedTokenResponse> {
    const endpoints = getFeedTokenEndpointCandidates();
    let lastError: Error | null = null;

    for (const endpoint of endpoints) {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller
            ? setTimeout(() => controller.abort(), FEED_TOKEN_REQUEST_TIMEOUT_MS)
            : null;

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                ...(controller ? { signal: controller.signal } : {}),
            });

            if (!response.ok) {
                const message = await parseErrorResponse(response);

                // Retry only for endpoint-mismatch style failures.
                const shouldRetry = response.status === 404 || response.status === 405;
                if (shouldRetry) {
                    lastError = new Error(message);
                    continue;
                }

                throw new Error(message);
            }

            const data = (await response.json()) as FeedTokenResponse;
            if (!data?.token) {
                throw new Error('Invalid feed token response from server.');
            }

            return data;
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                lastError = new Error('Feed token request timed out.');
                continue;
            }

            // Network errors (including CORS) should fall through to next candidate.
            if (error instanceof TypeError) {
                lastError = new Error('Could not reach the feed token endpoint.');
                continue;
            }
            lastError = error instanceof Error ? error : new Error('Failed to fetch feed token.');
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    try {
        return await fetchOrCreateFeedTokenViaSupabase();
    } catch (fallbackError: any) {
        const fallbackMessage = fallbackError?.message ? String(fallbackError.message) : '';
        if (fallbackMessage && isAuthRelatedMessage(fallbackMessage)) {
            throw new Error(fallbackMessage);
        }

        const lastMessage = lastError?.message ? String(lastError.message) : '';
        if (lastMessage && isAuthRelatedMessage(lastMessage)) {
            throw new Error(lastMessage);
        }

        throw new Error(GENERIC_FEED_TOKEN_ERROR);
    }
}

export function getAtomOrigin(): string {
    if (isLocalWebDevHost()) {
        return DEFAULT_LOCAL_API_BASE_URL;
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return trimTrailingSlash(window.location.origin);
    }

    return getConfiguredBaseUrl();
}

export function buildProjectAtomFeedUrl(projectId: string, token: string): string {
    const origin = getAtomOrigin();
    const encodedProjectId = encodeURIComponent(projectId);
    const encodedToken = encodeURIComponent(token);
    return `${origin}/api/feed/atom/${encodedProjectId}?token=${encodedToken}`;
}

export async function getProjectAtomFeedUrl(projectId: string): Promise<string> {
    if (!projectId) {
        throw new Error('Project ID is required.');
    }

    const accessToken = await getSessionAccessToken();
    const tokenResponse = await fetchOrCreateFeedToken(accessToken);
    return buildProjectAtomFeedUrl(projectId, tokenResponse.token);
}
